import {
  TypedOmit,
  TypedValue,
  TypeOf,
  ValueOf,
} from '@client/new/helpers/types';
import { PillProps } from './Pill';
import {
  ButtonLinkProps,
  ButtonProps,
} from '@design-system/components/button/button';

import { RowProps } from '@design-system/components/grid/grid';
import { TypographyProps } from '@design-system/components/typography/typography';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { IMenuItem } from '@client/new/components/TableShared/Cell';

type PositionListCell =
  | TypedValue<'col', ListCell[]>
  | TypedValue<'row', { data: ListCell[]; props: RowProps }>;

type TypographyListCell =
  | TypedValue<'title', string>
  | TypedValue<'text', string>
  | TypedValue<'small-text', string>
  | TypedValue<'bold-text', string>
  | TypedValue<'typography', TypographyProps & { content: string }>;

export type ButtonListCell =
  | TypedValue<
      'icon',
      TypedOmit<FontAwesomeIconProps, 'icon'> & {
        src: FontAwesomeIconProps['icon'];
      }
    >
  | TypedValue<'button', ButtonProps & { small?: boolean }>
  | TypedValue<'link', ButtonLinkProps & { small?: boolean }>
  | TypedValue<
      'linkButton',
      { data: ListCell[]; props: TypedOmit<ButtonLinkProps, 'size'> }
    >;

type CustomListCell =
  | TypedValue<'image', { src: string; width: string; height: string }>
  | TypedValue<'pill', TypedOmit<PillProps, 'tooltipProps' | 'tooltipRef'>>
  | TypedValue<'tooltip', { title: string; content: ListCell }>
  | TypedValue<'menu', IMenuItem[]>;

export type ListCell =
  | PositionListCell
  | TypographyListCell
  | ButtonListCell
  | CustomListCell;

export type ListCellType = TypeOf<ListCell>;
export type ListCellValue<T extends ListCellType> = ValueOf<ListCell, T>;

export const cell = <T extends ListCellType>(
  type: T,
  value: ListCellValue<T>,
): ListCell => ({ type, value } as ListCell);

const lazyCell =
  <T extends ListCellType>(type: T) =>
  (value: ListCellValue<T>): ListCell =>
    cell(type, value);

export const col = lazyCell('col');
export const row = (data: ListCell[], props: RowProps = {}) =>
  cell('row', { data, props });

export const title = lazyCell('title');
export const text = lazyCell('text');
export const smallText = lazyCell('small-text');
export const boldText = lazyCell('bold-text');
export const typography = lazyCell('typography');

export const icon = lazyCell('icon');
export const button = lazyCell('button');
export const linkButton = lazyCell('linkButton');
export const link = lazyCell('link');

export const pill = lazyCell('pill');
export const image = lazyCell('image');
export const tooltip = (title: string, content: ListCell) =>
  cell('tooltip', { title, content });
export const menu = (value: IMenuItem[]) => cell('menu', value);
export const menuItem = (
  text: string,
  onClick?: IMenuItem['onClick'],
  props: TypedOmit<IMenuItem, 'text' | 'onClick'> = {},
): IMenuItem => ({ text, onClick, ...props });

export { ListCellRenderer } from './ListCellRenderer';
